// src/components/resources/tools/generators/ColorPalette/ColorPaletteOutput.tsx
'use client'

import { useCallback } from 'react'
import {
  SparklesIcon,
  LightBulbIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'
import type { GeneratorField } from '~/lib/resources/types/tools/generator'
import type { HarmonyType, WcagLevel } from '~/lib/utils/colors'
import { getString, getStringArray } from '~/lib/utils/form-values'
import { ColorSwatch } from './ColorSwatch'
import type { ColorAnalysis, ColorFormat, ColorInfo } from './types'

/**
 * Output component that displays the final generated color palette
 * @param formData - The form data used to generate the palette
 * @param formatText - Function to format text with proper capitalization
 */
export function ColorPaletteOutput({
  formData,
  formatText,
  generatedColors,
  colorAnalysis,
  onCopyColor,
  copiedColor,
  getFieldValue,
  fields,
}: {
  formData: Record<string, unknown>
  formatText: (text: string) => string
  generatedColors: ColorInfo[]
  colorAnalysis: ColorAnalysis | null
  onCopyColor: (color: string, format?: ColorFormat) => Promise<void>
  copiedColor: string | null
  getFieldValue: (field: GeneratorField) => unknown
  fields: GeneratorField[]
}): JSX.Element {
  // Use getFieldValue to get effective values including defaults
  const getEffectiveValue = useCallback((fieldId: string, defaultValue: unknown = '') => {
    const field = fields.find(f => f.id === fieldId)
    if (field) {
      return getFieldValue(field) ?? defaultValue
    }
    return formData[fieldId] ?? defaultValue
  }, [fields, getFieldValue, formData])

  const colorName = getString(getEffectiveValue('colorName'), 'My Palette')
  const harmonyType = getString(getEffectiveValue('harmonyType'), 'monochromatic') as HarmonyType
  const accessibilityLevel = getString(getEffectiveValue('accessibilityLevel'), 'aa') as WcagLevel
  const usageType = getStringArray(getEffectiveValue('usageType'))
  const saturation = getString(getEffectiveValue('saturation'), 'balanced')
  const lightness = getString(getEffectiveValue('lightness'), 'medium')

  return (
    <div className="space-y-6">
      {/* Enhanced Color Swatches */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {/* Render swatches in the palette's OWN order (buildPalette HSL-sorts
            them), labeling each by its role NAME. Rendering in array order —
            rather than re-grouping by role in this one surface — keeps the grid,
            the live preview, and the exported HEX/RGB/HSL lists in the SAME
            order the user sees. The name is the single source of truth, so the
            base is labeled "Primary" wherever the sort places it. */}
        {generatedColors.map((color, index) => (
          <ColorSwatch
            key={`swatch-${index}`}
            color={color}
            copiedColor={copiedColor}
            onCopyColor={onCopyColor}
            label={color.name ?? `Color ${index + 1}`}
          />
        ))}
      </div>

      {/* Color Analysis */}
      {colorAnalysis && (
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <SparklesIcon className="h-5 w-5 text-indigo-600" />
            <h4 className="font-semibold text-gray-900 dark:text-white">Palette Analysis</h4>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-indigo-600">{colorAnalysis.harmonyScore}%</div>
              <div className="text-xs text-gray-600 dark:text-gray-400">Harmony</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{colorAnalysis.diversityScore}%</div>
              <div className="text-xs text-gray-600 dark:text-gray-400">Diversity</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">{Math.round(colorAnalysis.contrastScore)}%</div>
              <div className="text-xs text-gray-600 dark:text-gray-400">Contrast</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {formatText(colorAnalysis.temperatureBalance)}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">Temperature</div>
            </div>
          </div>

          {/* Recommendations */}
          {colorAnalysis.recommendations.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <LightBulbIcon className="h-4 w-4 text-yellow-600" />
                <span className="text-sm font-medium text-gray-900 dark:text-white">Recommendations</span>
              </div>
              <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
                {colorAnalysis.recommendations.map((rec, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="text-yellow-600 mt-1">•</span>
                    <span>{rec}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Warnings */}
          {colorAnalysis.warnings.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <ExclamationTriangleIcon className="h-4 w-4 text-red-600" />
                <span className="text-sm font-medium text-gray-900 dark:text-white">Warnings</span>
              </div>
              <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
                {colorAnalysis.warnings.map((warning, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="text-red-600 mt-1">•</span>
                    <span>{warning}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Palette Specifications */}
      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 dark:text-white mb-3">Palette Specifications</h4>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-600 dark:text-gray-400">Name:</span>
            <span className="ml-2 text-gray-900 dark:text-white">{colorName}</span>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-400">Harmony:</span>
            <span className="ml-2 text-gray-900 dark:text-white">{formatText(harmonyType)}</span>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-400">Size:</span>
            <span className="ml-2 text-gray-900 dark:text-white">{generatedColors.length} colors</span>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-400">Accessibility:</span>
            <span className="ml-2 text-gray-900 dark:text-white">{formatText(`wcag ${accessibilityLevel}`)}</span>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-400">Usage:</span>
            <span className="ml-2 text-gray-900 dark:text-white">
              {usageType.join(', ') || 'General'}
            </span>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-400">Style:</span>
            <span className="ml-2 text-gray-900 dark:text-white">{formatText(saturation)} • {formatText(lightness)}</span>
          </div>
        </div>
      </div>
    </div>
  )
}
